import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Truck, ArrowLeft, TrendingUp, Clock, Users, DollarSign, CheckCircle } from 'lucide-react';

const WholesaleCaseStudy = () => {
  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <article className="pt-40 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/industries" className="inline-flex items-center gap-2 text-[#2563EB] hover:text-[#3B82F6] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Industries
          </Link>
          
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                <Truck className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="text-sm text-[#2563EB] font-semibold uppercase tracking-wide">
                  Wholesale & Distribution
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#F8FAFC]">
                  Pacific Coast Supplies
                </h1>
              </div>
            </div>
            <p className="text-xl text-[#94A3B8]">
              How a regional distributor saved 80% of pricing admin time and grew B2B accounts by 24%
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden mb-12 border border-[#2563EB]/20">
            <img
              src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200"
              alt="Pacific Coast Supplies Warehouse"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C]/60 to-transparent" />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Users className="w-8 h-8 text-[#2563EB] mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">180</div>
              <div className="text-[#94A3B8]">B2B Accounts Managed</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <TrendingUp className="w-8 h-8 text-green-400 mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">2,800</div>
              <div className="text-[#94A3B8]">SKUs in Catalog</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Clock className="w-8 h-8 text-orange-400 mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">45 years</div>
              <div className="text-[#94A3B8]">Family-Owned Business</div>
            </div>
          </div>

          <div className="prose prose-invert max-w-none">
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-4">The Challenge</h2>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-4">
                Pacific Coast Supplies is a third-generation building materials distributor serving contractors across the Pacific Northwest. With 180 active B2B accounts and a catalog of 2,800 SKUs, their sales team was drowning in pricing complexity.
              </p>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-4">
                Each contractor had negotiated their own discount structure over the years. Some got 15% off lumber, others got 20% off hardware. Volume commitments, loyalty agreements, and one-off deals created a tangled web that only the most senior salespeople could navigate.
              </p>
              <p className="text-[#94A3B8] text-lg leading-relaxed">
                The result? Sales reps spent 4+ hours daily just managing price lists and generating quotes. New hires took 6 months to learn the pricing system. And the company had no clear visibility into which accounts were actually profitable.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-4">The Solution</h2>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-6">
                PriceStrike helped Pacific Coast Supplies transform their chaotic pricing into a systematic, automated framework:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Automated Tier System:</span>
                    <span className="text-[#94A3B8]"> Created 5 clear contractor tiers based on annual spend, with automatic progression as accounts grow.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Smart Discount Rules:</span>
                    <span className="text-[#94A3B8]"> Replaced ad-hoc negotiations with category-based discount structures that maintain margins while rewarding volume.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Instant Quote Generation:</span>
                    <span className="text-[#94A3B8]"> Any sales rep can now generate accurate quotes in seconds, with customer-specific pricing applied automatically.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Account Profitability Dashboard:</span>
                    <span className="text-[#94A3B8]"> Real-time visibility into margin by customer, revealing which accounts deserve more attention and which need renegotiation.</span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-green-500/10 to-[#111827] border border-green-500/30 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-6">The Results</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">80%</div>
                  <div className="text-[#94A3B8]">Reduction in pricing admin time</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">24%</div>
                  <div className="text-[#94A3B8]">Growth in B2B accounts</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">4 hrs</div>
                  <div className="text-[#94A3B8]">Daily time savings per rep</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">2 weeks</div>
                  <div className="text-[#94A3B8]">New hire ramp-up time (vs. 6 months)</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#2563EB]/10 to-[#111827] border border-[#2563EB]/30 rounded-2xl p-8 mb-12">
              <blockquote className="text-xl text-[#F8FAFC] italic mb-4">
                "Our sales team used to spend half their day on pricing. Now they actually sell. The automated tier system means every contractor knows exactly what discount they're getting—no more negotiating every single order."
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">MR</span>
                </div>
                <div>
                  <div className="text-[#F8FAFC] font-semibold">Mike Rodriguez</div>
                  <div className="text-[#94A3B8]">VP of Sales, Pacific Coast Supplies</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Link to="/coming-soon">
              <button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-4 rounded-lg font-semibold transition-colors">
                Start Your Free Trial
              </button>
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default WholesaleCaseStudy;
